import { ref, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

const DEMO_DURATION_MS = 30 * 60 * 1000 // 30 minutes
const LOCKOUT_DURATION_MS = 5 * 60 * 1000 // 5 minutes

const STORAGE_KEY_START = 'demo_session_start'
const STORAGE_KEY_LOCKOUT = 'demo_lockout_until'

// Shared global state to ensure synchronization between components
const remainingTime = ref(DEMO_DURATION_MS)
const remainingLockoutTime = ref(0)
const isLocked = ref(false)

// Shared timer control
let timerInterval: number | null = null
let subscribers = 0

function formatTime(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

function tick() {
  const authStore = useAuthStore()
  // Check if current user is demo/viewer
  const isDemo = authStore.user?.role?.toLowerCase() === 'viewer'

  if (!isDemo) {
    // If not demo, ensure we don't lock the screen
    isLocked.value = false
    return
  }

  const now = Date.now()

  // 1. Check if we are currently locked out
  const lockoutUntil = localStorage.getItem(STORAGE_KEY_LOCKOUT)
  if (lockoutUntil) {
    const lockoutTime = parseInt(lockoutUntil)
    if (now < lockoutTime) {
      // Still locked out
      isLocked.value = true
      remainingLockoutTime.value = lockoutTime - now
      remainingTime.value = 0
      return
    } else {
      // Lockout finished, reset session for new 30 mins
      localStorage.removeItem(STORAGE_KEY_LOCKOUT)
      localStorage.setItem(STORAGE_KEY_START, now.toString())
      isLocked.value = false
    }
  }

  // 2. Check active session time
  let start = localStorage.getItem(STORAGE_KEY_START)
  if (!start) {
    start = now.toString()
    localStorage.setItem(STORAGE_KEY_START, start)
  }

  const startTime = parseInt(start)
  const elapsed = now - startTime

  if (elapsed >= DEMO_DURATION_MS) {
    // Time's up, trigger lockout
    const newLockoutUntil = now + LOCKOUT_DURATION_MS
    localStorage.setItem(STORAGE_KEY_LOCKOUT, newLockoutUntil.toString())
    localStorage.removeItem(STORAGE_KEY_START)
    isLocked.value = true
    remainingLockoutTime.value = LOCKOUT_DURATION_MS
    remainingTime.value = 0
  } else {
    // Session Active
    isLocked.value = false
    remainingTime.value = Math.max(0, DEMO_DURATION_MS - elapsed)
  }
}

export function useDemoTimer() {
  const authStore = useAuthStore()
  const isDemo = computed(() => authStore.user?.role?.toLowerCase() === 'viewer')

  function startTimer() {
    subscribers++
    if (!timerInterval) {
      tick() // Initial check
      timerInterval = window.setInterval(tick, 1000)
    }
  }

  function stopTimer() {
    subscribers--
    if (subscribers <= 0) {
      if (timerInterval) {
        window.clearInterval(timerInterval)
        timerInterval = null
      }
      subscribers = 0
    }
  }

  function resetSession() {
    localStorage.removeItem(STORAGE_KEY_START)
    localStorage.removeItem(STORAGE_KEY_LOCKOUT)
    tick()
  }

  return {
    isDemo,
    isLocked,
    remainingTime,
    remainingLockoutTime,
    formatTime,
    startTimer,
    stopTimer,
    resetSession
  }
}
